import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Loader2, ArrowLeft, Mail, ShieldCheck, AlertCircle, Eye, EyeOff, Stethoscope, User, UserCog, LogOut, PlayCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { getDashboardRouteForRole } from "@/utils/roleRedirects";
import LoginDemoAnimation from "@/components/login/LoginDemoAnimation";
import PlatformDemoModal from "@/components/demo/PlatformDemoModal";

type LoginStep = "demo" | "email" | "patient-otp" | "admin-password" | "admin-otp";
type UserType = "patient" | "admin" | null;

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: patientUser, loading: patientAuthLoading, signOut: patientSignOut } = useAuth();
  const { user: adminUser, signIn: adminSignIn, verifyOtp: adminVerifyOtp, loading: adminAuthLoading, signOut: adminSignOut } = useAdminAuth();
  
  const [showExistingSession, setShowExistingSession] = useState(false);
  const [actualUserRole, setActualUserRole] = useState<string | null>(null);
  const [actualUserRoleLoading, setActualUserRoleLoading] = useState(true);
  const [actualUserRoleError, setActualUserRoleError] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(false);
  // Check if user has seen the demo before
  const hasSeenDemo = localStorage.getItem("login_demo_seen") === "true";
  const [step, setStep] = useState<LoginStep>(hasSeenDemo ? "email" : "demo");
  const [userType, setUserType] = useState<UserType>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [form, setForm] = useState({
    email: "",
    password: "",
    otp: ""
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [agencyInfo, setAgencyInfo] = useState<{ name: string; maskedEmail?: string } | null>(null);
  const [showPlatformDemo, setShowPlatformDemo] = useState(false);
  
  // Fetch actual role from user_roles table when session exists
  useEffect(() => {
    const fetchActualRole = async () => {
      const currentUser = adminUser || patientUser;

      setActualUserRoleError(null);

      if (!currentUser?.id) {
        setActualUserRole(null);
        setActualUserRoleLoading(false);
        return;
      }

      // First try admin context role (most reliable)
      if (adminUser?.role) {
        setActualUserRole(adminUser.role);
        setActualUserRoleLoading(false);
        return;
      }

      setActualUserRoleLoading(true);

      // Query user_roles table for the actual role (single source of truth)
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', currentUser.id);

        if (error) {
          setActualUserRoleError(error.message);
          setActualUserRole(null);
          return;
        }

        const roles = (data || []).map((r) => r.role);
        const computedRole = roles.includes('super_admin')
          ? 'super_admin'
          : roles.includes('agency_admin')
            ? 'agency_admin'
            : roles.includes('supervisor')
              ? 'supervisor'
              : roles.length > 0
                ? 'patient'
                : null;

        setActualUserRole(computedRole);
      } catch (e) {
        setActualUserRoleError(e instanceof Error ? e.message : 'Role lookup failed');
        setActualUserRole(null);
      } finally {
        setActualUserRoleLoading(false);
      }
    };

    fetchActualRole();
  }, [adminUser, patientUser]);

  // Handle session state - show existing session UI OR redirect after successful login
  useEffect(() => {
    // If user just completed login (step is past email entry), redirect to dashboard
    if (adminUser && !adminAuthLoading && (step === "admin-password" || step === "admin-otp")) {
      navigate(getDashboardRouteForRole(adminUser.role));
      return;
    }

    // If user just completed patient login, redirect (wait for role to load to avoid mis-routing admins)
    if (patientUser && !patientAuthLoading && step === "patient-otp") {
      if (!patientUser.role) return;

      // Patient-only onboarding
      if (
        patientUser.role === "patient" &&
        (patientUser.patient?.first_name === "New" || !patientUser.patient?.first_name)
      ) {
        navigate("/complete-profile");
      } else {
        navigate(getDashboardRouteForRole(patientUser.role));
      }
      return;
    }
    
    // Show existing session UI for users who are already logged in and on the initial step
    if (step === "email" || step === "demo") {
      if ((patientUser && !patientAuthLoading) || (adminUser && !adminAuthLoading)) {
        setShowExistingSession(true);
      } else {
        setShowExistingSession(false);
      }
    }
  }, [patientUser, patientAuthLoading, adminUser, adminAuthLoading, step, navigate]);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      if (adminUser) {
        await adminSignOut();
      }
      if (patientUser) {
        await patientSignOut();
      }
      await supabase.auth.signOut();
      setShowExistingSession(false);
      toast({
        title: "Signed out",
        description: "You can now sign in with a different account.",
      });
    } catch (err) {
      console.error("Sign out error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Force logout clears everything including local storage
  const handleForceLogout = async () => {
    setLoading(true);
    try {
      // Clear all local storage related to auth
      localStorage.clear();
      sessionStorage.clear();
      
      // Try to sign out from Supabase (both global and local)
      try {
        await supabase.auth.signOut();
      } catch {
        // Ignore - try local signout as fallback
      }
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Ignore
      }
      
      setShowExistingSession(false);
      
      toast({
        title: "Force logout complete",
        description: "All session data has been cleared. Please sign in again.",
      });
      
      // Hard reload to ensure all state is cleared
      window.location.reload();
    } catch (err) {
      console.error("Force logout error:", err);
      // Even on error, try to reload
      window.location.reload();
    }
  };

  const handleContinueToApp = () => {
    // Use the actual role from user_roles table
    const effectiveRole = actualUserRole || adminUser?.role;
    
    // Prefer admin context when available
    if (adminUser) {
      navigate(getDashboardRouteForRole(adminUser.role));
      return;
    }

    if (!patientUser) return;

    // If role not loaded yet, avoid mis-routing super admins to patient onboarding
    if (!effectiveRole) {
      toast({
        title: "Loading profile…",
        description: "Please wait a moment and try again.",
      });
      return;
    }

    // Patient-only onboarding
    if (effectiveRole === "patient") {
      if (patientUser.patient?.first_name === "New" || !patientUser.patient?.first_name) {
        navigate("/complete-profile");
      } else {
        navigate(getDashboardRouteForRole(effectiveRole));
      }
      return;
    }

    // Non-patient roles (e.g., super_admin) should never go to patient onboarding
    navigate(getDashboardRouteForRole(effectiveRole));
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const emailLower = form.email.trim().toLowerCase();
      
      // Call edge function to check user type and handle appropriately
      const response = await supabase.functions.invoke("patient-request-otp", {
        body: { email: emailLower, check_type: true }
      });

      const data = response.data;
      
      // Check if user is an admin (edge function will tell us)
      if (data?.user_type === "admin") {
        setUserType("admin");
        setStep("admin-password");
        return;
      }

      // Handle errors
      if (response.error || data?.error) {
        const errorCode = data?.error_code;
        const errorMessage = data?.error || response.error?.message;
        
        if (errorCode === "NOT_REGISTERED") {
          setError("This email is not registered. Please contact your healthcare provider to get registered.");
          return;
        }
        if (errorCode === "AGENCY_NOT_APPROVED") {
          setError("Your healthcare provider is not yet approved to use this platform. Please contact them for assistance.");
          return;
        }
        throw new Error(errorMessage || "Failed to verify email");
      }

      // Patient flow - OTP was sent
      setUserType("patient");
      setAgencyInfo({
        name: data.agency_name,
        maskedEmail: data.masked_email
      });
      setStep("patient-otp");
      setResendCooldown(30);

      toast({
        title: "Verification Code Sent",
        description: `A code has been sent to ${data.masked_email || "your email"}`,
      });
    } catch (err: any) {
      console.error("Email check error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke("patient-verify-otp", {
        body: {
          email: form.email.trim().toLowerCase(),
          otp: form.otp
        }
      });

      if (response.error) {
        throw new Error(response.error.message || "Verification failed");
      }

      const data = response.data;

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });
      }

      toast({
        title: "Welcome!",
        description: "Successfully signed in",
      });
    } catch (err: any) {
      console.error("OTP verify error:", err);
      setError(err.message || "Invalid or expired verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleAdminPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await adminSignIn(form.email.trim().toLowerCase(), form.password);
      
      if (result.error) {
        setError(result.error);
      } else if (result.requires2FA) {
        setStep("admin-otp");
        setResendCooldown(30);
        toast({
          title: "Verification code sent",
          description: "Please check your email for the 6-digit code.",
        });
      } else {
        if (rememberMe) {
          localStorage.setItem('admin_remember_me', 'true');
        } else {
          localStorage.removeItem('admin_remember_me');
        }
        toast({
          title: "Welcome back!",
          description: "Successfully signed in",
        });
        // The adminUser context will be set by signIn, so we navigate after a brief delay
        // to ensure context is updated, or rely on useEffect to handle redirect
      }
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAdminOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.otp.length !== 6) return;
    
    setLoading(true);
    setError(null);

    try {
      const result = await adminVerifyOtp(form.email.trim().toLowerCase(), form.password, form.otp);
      
      if (result.error) {
        setError(result.error);
      } else {
        if (rememberMe) {
          localStorage.setItem('admin_remember_me', 'true');
        }
        toast({
          title: "Welcome back!",
          description: "Successfully signed in",
        });
        // Role-based redirect handled by useEffect watching adminUser
      }
    } catch (err: any) {
      setError(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setForm(prev => ({ ...prev, otp: "" }));
    
    if (userType === "patient") {
      await handleEmailSubmit(new Event("submit") as any);
    } else if (userType === "admin") {
      setLoading(true);
      try {
        const result = await adminSignIn(form.email.trim().toLowerCase(), form.password);
        if (result.requires2FA) {
          setResendCooldown(30);
          toast({
            title: "Code resent",
            description: "A new verification code has been sent to your email.",
          });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setStep("email");
    setUserType(null);
    setForm({ email: "", password: "", otp: "" });
    setAgencyInfo(null);
    setError(null);
    setResendCooldown(0);
  };

  const handleDemoComplete = () => {
    localStorage.setItem("login_demo_seen", "true");
    setStep("email");
  };

  const handleDemoSkip = () => {
    localStorage.setItem("login_demo_seen", "true");
    setStep("email");
  };

  if (patientAuthLoading || adminAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show existing session UI
  if (showExistingSession && (patientUser || adminUser)) {
    const currentUser = adminUser || patientUser;
    const userEmail = adminUser?.email || patientUser?.email || "your account";
    const userRole = actualUserRole || adminUser?.role;

    return (
      <>
        <Helmet>
          <title>Already Signed In - CheckFirst</title>
        </Helmet>
        
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 gradient-hero rounded-2xl flex items-center justify-center">
                  <Stethoscope className="h-7 w-7 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-foreground">CheckFirst</h1>
              <p className="text-muted-foreground mt-2">You're already signed in</p>
            </div>

            <Card className="health-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  Active Session
                </CardTitle>
                <CardDescription>
                  You're currently signed in as:
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="font-medium text-foreground">{userEmail}</p>

                  <Badge variant="secondary" className="gap-1.5">
                    {actualUserRoleLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : userRole === "super_admin" || userRole === "agency_admin" || userRole === "supervisor" ? (
                      <UserCog className="h-3 w-3" />
                    ) : userRole === "patient" ? (
                      <User className="h-3 w-3" />
                    ) : null}

                    {actualUserRoleLoading
                      ? "Loading role…"
                      : userRole === "super_admin"
                        ? "Super Admin"
                        : userRole === "agency_admin"
                          ? "Agency Admin"
                          : userRole === "supervisor"
                            ? "Supervisor"
                            : userRole === "patient"
                              ? "Patient"
                              : "Unknown"}
                  </Badge>

                  {actualUserRoleError && (
                    <p className="text-xs text-destructive">
                      Role lookup failed: {actualUserRoleError}. Use Force Logout.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Button 
                    onClick={handleContinueToApp} 
                    className="w-full"
                    disabled={actualUserRoleLoading || !!actualUserRoleError}
                  >
                    Continue to Dashboard
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleSignOut} 
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <LogOut className="w-4 h-4 mr-2" />
                    )}
                    Sign Out & Use Different Account
                  </Button>
                </div>

                <div className="border-t pt-4 mt-4">
                  <p className="text-xs text-muted-foreground text-center mb-2">
                    Having trouble signing out?
                  </p>
                  <Button 
                    variant="destructive" 
                    onClick={handleForceLogout} 
                    className="w-full"
                    disabled={loading}
                    size="sm"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertCircle className="w-4 h-4 mr-2" />
                    )}
                    Force Logout (Clear All Data)
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    This clears all cached data and reloads the page.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // Show demo animation first
  if (step === "demo") {
    return (
      <>
        <Helmet>
          <title>Welcome - CheckFirst</title>
        </Helmet>
        <AnimatePresence mode="wait">
          <LoginDemoAnimation 
            onComplete={handleDemoComplete} 
            onSkip={handleDemoSkip} 
          />
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign In - CheckFirst</title>
        <meta name="description" content="Sign in to CheckFirst. Access AI-powered healthcare triage for patients and healthcare providers." />
        <link rel="canonical" href="https://www.checkfirstcare.com/login" />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 gradient-hero rounded-2xl flex items-center justify-center">
                <Stethoscope className="h-7 w-7 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-foreground">CheckFirst</h1>
            <p className="text-muted-foreground mt-2">
              {step === "email" && "Sign in to your account"}
              {step === "patient-otp" && "Patient Verification"}
              {step === "admin-password" && "Healthcare Provider Sign In"}
              {step === "admin-otp" && "Two-Factor Authentication"}
            </p>
            
            {/* Role Detection Indicator */}
            {userType && step !== "email" && (
              <div className="mt-4 flex justify-center animate-fade-in">
                <Badge 
                  variant={userType === "admin" ? "default" : "secondary"}
                  className={`gap-1.5 px-3 py-1.5 text-sm animate-scale-in ${
                    userType === "admin" 
                      ? "bg-primary/10 text-primary border-primary/20 hover:bg-primary/10" 
                      : "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/10 dark:text-emerald-400"
                  }`}
                >
                  {userType === "admin" ? (
                    <>
                      <UserCog className="h-3.5 w-3.5 animate-pulse" />
                      Healthcare Provider
                    </>
                  ) : (
                    <>
                      <User className="h-3.5 w-3.5 animate-pulse" />
                      Patient Account
                    </>
                  )}
                </Badge>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card className="health-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                {step === "email" && "Secure Sign In"}
                {step === "patient-otp" && "Enter Verification Code"}
                {step === "admin-password" && "Enter Password"}
                {step === "admin-otp" && "Enter Verification Code"}
              </CardTitle>
              <CardDescription>
                {step === "email" && "Enter your email to continue"}
                {step === "patient-otp" && `A code has been sent to ${agencyInfo?.maskedEmail || "your email"}`}
                {step === "admin-password" && "Enter your password to access the admin panel"}
                {step === "admin-otp" && `We've sent a 6-digit code to ${form.email}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Email Step */}
              {step === "email" && (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={form.email}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, email: e.target.value }));
                          setError(null);
                        }}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading || !form.email}
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Continue
                  </Button>

                  <div className="text-center mt-4 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Patients and healthcare providers can sign in here.
                      Your account type will be detected automatically.
                    </p>
                    
                    {/* Take a Tour button for first-time users */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setShowPlatformDemo(true)}
                    >
                      <PlayCircle className="h-4 w-4" />
                      Take a Tour
                    </Button>
                  </div>
                </form>
              )}

              {/* Patient OTP Step */}
              {step === "patient-otp" && (
                <form onSubmit={handlePatientOtpSubmit} className="space-y-6">
                  {agencyInfo && (
                    <div className="bg-muted/50 rounded-lg p-3 text-sm">
                      <p className="font-medium">Provider: {agencyInfo.name}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Verification Code</Label>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={form.otp}
                        onChange={(value) => setForm(prev => ({ ...prev, otp: value }))}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading || form.otp.length !== 6}
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Verify & Sign In
                  </Button>

                  <div className="flex justify-between items-center text-sm">
                    <Button
                      type="button"
                      variant="link"
                      onClick={resetForm}
                      disabled={loading}
                      className="p-0 h-auto"
                    >
                      Use different email
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      onClick={handleResendOtp}
                      disabled={loading || resendCooldown > 0}
                      className="p-0 h-auto"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </Button>
                  </div>
                </form>
              )}

              {/* Admin Password Step */}
              {step === "admin-password" && (
                <form onSubmit={handleAdminPasswordSubmit} className="space-y-6">
                  <div className="bg-primary/10 rounded-lg p-3 text-sm flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-primary" />
                    <span>Healthcare Provider Account</span>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={form.password}
                        onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                        className="pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute inset-y-0 right-0 px-3 py-0 h-full"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                      />
                      <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer">
                        Remember me
                      </Label>
                    </div>
                    <Link
                      to="/admin/forgot-password"
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading || !form.password}
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Sign In
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={resetForm}
                  >
                    Use different email
                  </Button>
                </form>
              )}

              {/* Admin OTP Step */}
              {step === "admin-otp" && (
                <form onSubmit={handleAdminOtpSubmit} className="space-y-6">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    
                    <InputOTP
                      maxLength={6}
                      value={form.otp}
                      onChange={(value) => setForm(prev => ({ ...prev, otp: value }))}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      disabled={resendCooldown > 0 || loading}
                      onClick={handleResendOtp}
                      className="text-sm"
                    >
                      {resendCooldown > 0
                        ? `Resend code in ${resendCooldown}s`
                        : "Didn't receive a code? Resend"}
                    </Button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || form.otp.length !== 6}
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Verify & Sign In
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setStep("admin-password");
                      setForm(prev => ({ ...prev, otp: "" }));
                    }}
                  >
                    Back to password
                  </Button>
                </form>
              )}

              {/* Register link for new providers */}
              {step === "email" && (
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground text-center">
                    Healthcare provider?{" "}
                    <Link
                      to="/admin/register"
                      className="text-primary hover:text-primary/80 font-medium"
                    >
                      Register your agency
                    </Link>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Platform Demo Modal */}
      <PlatformDemoModal open={showPlatformDemo} onClose={() => setShowPlatformDemo(false)} />
    </>
  );
};

export default UnifiedLoginPage;
